"use client";

import { type ReactNode, useMemo } from "react";
import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  TABLE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  countPrompts,
  type DocBlock,
  type DocGridCell,
  type DocList,
  type DocRun,
  type DocText,
  type DocumentLabels,
  type DocumentModel,
  documentToHtml,
  documentToMarkdown,
  documentToPrintHtml,
  documentToText,
  isBlankText,
  listMarker,
} from "@/lib/tool-kit/document";
import { downloadText } from "@/lib/tool-kit/download";
import { printHtmlDocument } from "@/lib/tool-kit/print";

/**
 * The output half of every template generator: a structured document, previewed, with
 * Copy, two downloads and Print.
 *
 * A tool builds a `DocumentModel` (`lib/tool-kit/document.ts`) from its form with a
 * pure function and passes it in; this component draws it and offers it four ways:
 *
 * - **Copy** puts rich HTML and a plain-text twin on the clipboard, so headings, lists,
 *   tables and italics survive a paste into Word or Google Docs.
 * - **Download .txt / .md** saves the same document as plain text (underlined headings,
 *   aligned tables) or Markdown (task lists, pipe tables).
 * - **Print** prints the document alone, typeset for paper, through a hidden frame — not
 *   the page around it, whatever the page's own print mode is.
 *
 * Prompts (the parts the writer still has to write) are drawn as dashed boxes and
 * counted in a polite live region, which is also how a screen-reader user hears the
 * document change while they type into the form above it.
 *
 * Every visible word arrives in `labels`, like the kit's primitives:
 *
 * ```tsx
 * const doc = useMemo(() => buildLabReport(input, strings), [input, strings]);
 * <DocumentPreview
 *   doc={doc}
 *   fileName={strings.fileName}            // "lab-report" → lab-report.txt / .md
 *   idPrefix="lr"                          // unique on the page
 *   labels={{
 *     heading: strings.documentHeading,    // the widget's results <h2>
 *     prompt: strings.toWrite,             // "To write" — no trailing colon
 *     guidance: strings.tip,               // "Tip"
 *     copy, copied, downloadText, downloadMarkdown, print,
 *     remaining: strings.remaining,        // "one: part still to write|other: parts still to write"
 *     complete: strings.complete,          // shown when no prompts are left
 *   }}
 * />
 * ```
 *
 * Heading levels assume the page's own H1: the preview heading is an H2, the document
 * title an H3 and its sections H4. Pass `labels` as a memoised object — the text and
 * HTML serialisations are memoised on it.
 */

export interface DocumentPreviewLabels extends DocumentLabels {
  /** The preview's H2, e.g. "Your lab report template". */
  heading: string;
  copy: string;
  copied: string;
  downloadText: string;
  downloadMarkdown: string;
  print: string;
  /** A plural message for the prompt counter: "one: part still to write|other: …". */
  remaining: string;
  /** Shown instead of the counter once no prompts are left. */
  complete: string;
}

const PARAGRAPH = "text-foreground text-sm leading-relaxed";
const GUIDANCE = "text-muted-foreground text-xs leading-relaxed";
const PROMPT_BOX =
  "rounded-md border border-primary/40 border-dashed bg-primary/5 px-3 py-2 text-foreground text-sm leading-relaxed print:bg-transparent";
const PROMPT_INLINE = "text-muted-foreground text-sm italic leading-relaxed";
const CELL =
  "border-border border-b px-3 py-2 align-top text-foreground text-sm";
const HEAD_CELL =
  "border-border border-b px-3 py-2 text-left align-bottom font-medium text-foreground text-xs";

function RunView({ run }: { run: DocRun }) {
  let node: ReactNode = run.text;
  if (run.italic) {
    node = <em>{node}</em>;
  }
  if (run.bold) {
    node = <strong>{node}</strong>;
  }
  return node;
}

function Runs({ text }: { text: DocText }) {
  if (typeof text === "string") {
    return text;
  }
  return (
    <>
      {text.map((run, index) => (
        // Runs are positional pieces of one line and never reorder.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional runs
        <RunView key={index} run={run} />
      ))}
    </>
  );
}

function PromptLabel({ label }: { label: string }) {
  return <span className="font-medium text-primary">{label}: </span>;
}

function ListView({ list, labels }: { list: DocList; labels: DocumentLabels }) {
  const start = list.start ?? 1;
  const items = list.items.map((item, index) => (
    // Items are positional: an outline's "III." is its place in the list.
    // biome-ignore lint/suspicious/noArrayIndexKey: positional list items
    <li className="flex gap-2" key={index}>
      <span className="shrink-0 text-muted-foreground text-sm tabular-nums leading-relaxed">
        {listMarker(list.style, start + index)}
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        {item.prompt ? (
          <p className={PROMPT_INLINE}>
            [{labels.prompt}: <Runs text={item.text} />]
          </p>
        ) : (
          <p className={PARAGRAPH}>
            <Runs text={item.text} />
          </p>
        )}
        {item.children ? (
          <ListView labels={labels} list={item.children} />
        ) : null}
      </div>
    </li>
  ));
  if (list.style === "bullet") {
    return <ul className="space-y-1.5">{items}</ul>;
  }
  return <ol className="space-y-1.5">{items}</ol>;
}

function FieldsView({
  fields,
}: {
  fields: Extract<DocBlock, { kind: "fields" }>["fields"];
}) {
  return (
    <div className="space-y-1">
      {fields.map((field) => (
        <p className={PARAGRAPH} key={field.label}>
          <span className="font-medium">{field.label}:</span>{" "}
          {isBlankText(field.value) ? (
            <span
              aria-hidden="true"
              className="inline-block h-4 w-48 border-foreground/40 border-b align-baseline"
            />
          ) : (
            <Runs text={field.value ?? ""} />
          )}
        </p>
      ))}
    </div>
  );
}

function TableView({ block }: { block: Extract<DocBlock, { kind: "table" }> }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border print:overflow-visible">
      <table className={TABLE}>
        {block.caption ? (
          <caption className="px-3 pt-3 text-left font-medium text-foreground text-sm">
            <Runs text={block.caption} />
          </caption>
        ) : null}
        {block.head ? (
          <thead>
            <tr>
              {block.head.map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional columns
                <th className={HEAD_CELL} key={index} scope="col">
                  <Runs text={cell} />
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {block.rows.map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={`${CELL} h-9`} key={cellIndex}>
                  <Runs text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridCellView({
  cell,
  labels,
}: {
  cell: DocGridCell;
  labels: DocumentLabels;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border p-4 print:break-inside-avoid">
      <div>
        <p className="font-semibold text-foreground">{cell.title}</p>
        {cell.caption ? <p className={GUIDANCE}>{cell.caption}</p> : null}
      </div>
      {cell.items.length > 0 ? (
        <ul className="list-disc space-y-1 ps-5 text-foreground text-sm leading-relaxed">
          {cell.items.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional items
            <li key={index}>
              <Runs text={item} />
            </li>
          ))}
        </ul>
      ) : (
        <p className={PROMPT_BOX}>
          <PromptLabel label={labels.prompt} />
          <Runs text={cell.prompt ?? ""} />
        </p>
      )}
    </div>
  );
}

function BlockView({
  block,
  labels,
}: {
  block: DocBlock;
  labels: DocumentLabels;
}) {
  switch (block.kind) {
    case "subheading":
      return (
        <h5 className="pt-1 font-semibold text-foreground text-sm">
          <Runs text={block.text} />
        </h5>
      );
    case "paragraph":
      return (
        <p className={PARAGRAPH}>
          <Runs text={block.text} />
        </p>
      );
    case "list":
      return <ListView labels={labels} list={block.list} />;
    case "fields":
      return <FieldsView fields={block.fields} />;
    case "prompt":
      return (
        <p className={PROMPT_BOX}>
          <PromptLabel label={labels.prompt} />
          <Runs text={block.text} />
        </p>
      );
    case "guidance":
      return (
        <p className={GUIDANCE}>
          <span className="font-medium">{labels.guidance}:</span>{" "}
          <Runs text={block.text} />
        </p>
      );
    case "checklist":
      return (
        <ul className="space-y-1">
          {block.items.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional items
            <li className={`flex gap-2 ${PARAGRAPH}`} key={index}>
              <span aria-hidden="true">{item.checked ? "☑" : "☐"}</span>
              <span>
                <Runs text={item.text} />
              </span>
            </li>
          ))}
        </ul>
      );
    case "table":
      return <TableView block={block} />;
    case "grid":
      return (
        <div className="grid gap-3 sm:grid-cols-2 print:grid-cols-2">
          {block.cells.map((cell) => (
            <GridCellView cell={cell} key={cell.title} labels={labels} />
          ))}
        </div>
      );
    default:
      return (
        <div aria-hidden="true">
          {Array.from({ length: block.count }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: identical blank lines
            <div className="h-8 border-foreground/30 border-b" key={index} />
          ))}
        </div>
      );
  }
}

export function DocumentPreview({
  doc,
  labels,
  fileName,
  idPrefix,
}: {
  doc: DocumentModel;
  labels: DocumentPreviewLabels;
  /** The download name without an extension: "lab-report". */
  fileName: string;
  /** Prefixes the ids this component sets; must be unique on the page. */
  idPrefix: string;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const text = useMemo(() => documentToText(doc, labels), [doc, labels]);
  const richText = useMemo(() => documentToHtml(doc, labels), [doc, labels]);
  const remaining = countPrompts(doc);
  const headingId = `${idPrefix}-document-heading`;
  const titleId = `${idPrefix}-document-title`;

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h2 className={WIDGET_H2} id={headingId}>
          {labels.heading}
        </h2>
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={labels.copied}
            html={richText}
            label={labels.copy}
            small
            text={text}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() => downloadText(`${fileName}.txt`, text)}
            type="button"
          >
            {labels.downloadText}
          </button>
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              downloadText(
                `${fileName}.md`,
                documentToMarkdown(doc, labels),
                "text/markdown;charset=utf-8"
              )
            }
            type="button"
          >
            {labels.downloadMarkdown}
          </button>
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              printHtmlDocument(documentToPrintHtml(doc, labels, locale))
            }
            type="button"
          >
            {labels.print}
          </button>
        </div>
      </div>
      <p aria-live="polite" className={`${HINT} print:hidden`}>
        {remaining > 0
          ? `${fmt(remaining)}${tPlural(labels.remaining, locale, remaining)}`
          : labels.complete}
      </p>
      <article
        aria-labelledby={titleId}
        className="space-y-6 rounded-lg border border-border bg-card p-5 sm:p-8 print:border-0 print:p-0"
      >
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-xl" id={titleId}>
            <Runs text={doc.title} />
          </h3>
          {doc.subtitle ? (
            <p className="text-muted-foreground text-sm">
              <Runs text={doc.subtitle} />
            </p>
          ) : null}
        </div>
        {doc.sections.map((section, sectionIndex) => (
          // Sections are positional; several have no heading to key on.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional sections
          <div className="space-y-3" key={sectionIndex}>
            {section.heading ? (
              <h4 className="border-border border-b pb-1 font-semibold text-base text-foreground">
                <Runs text={section.heading} />
              </h4>
            ) : null}
            {section.blocks.map((block, blockIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: positional blocks
              <BlockView block={block} key={blockIndex} labels={labels} />
            ))}
          </div>
        ))}
      </article>
    </section>
  );
}
